import React, { useState, useEffect } from 'react';
import { styles, colors } from '../styles/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import SortDrawer from './SortDrawer';
import FilterDrawer from './FilterDrawer';
import PageHeader from './PageHeader';
import StaggeredAnimation from './common/StaggeredAnimation';
import SortButton from './common/SortButton';
import SearchInput from './common/SearchInput';
import { useTagFilters, getPopularTags } from '../utils/tagUtils';
import useSortState from '../hooks/useSortState';
import { filterThoughtsByTags, filterThoughtsBySearchTerm } from '../utils/filterUtils';
import { sortThoughts } from '../utils/sortUtils';
import PlusIcon from './common/PlusIcon';
import IconButton from './common/IconButton';
import TagFilters from './TagFilters';
import ToolsBar from './common/ToolsBar';
import Identicon from './common/Identicon';

/**
 * Clean, minimal Spotify-style feed for thoughts
 */
const ThoughtsList = ({ thoughts, title, onDeleteThought, showBackButton = false, onBackClick, onAddClick, defaultSort }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const isDefaultCollection = location.state?.isDefault;
    const isYourThoughts = title === "Your Thoughts" && isDefaultCollection;
    
    // Use defaultSort prop if provided, otherwise use the original logic
    const initialSort = defaultSort || (isYourThoughts ? 'recent' : 'importance');
    const [sortBy, handleSortChange] = useSortState(initialSort);
    const [showSortDrawer, setShowSortDrawer] = useState(false);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const { activeFilters, setActiveFilters, toggleTag, hasActiveFilters } = useTagFilters({ tags: [] });
    
    // Extract and count tags
    const popularTags = getPopularTags(thoughts);

    // First filter out thoughts that don't belong in the library (those without theme, motif, or tags)
    const libraryThoughts = thoughts.filter(thought => 
        thought.theme || thought.motif || (thought.tags && thought.tags.length > 0)
    );

    // Apply filters using the utility functions
    const tagFilteredThoughts = filterThoughtsByTags(libraryThoughts, activeFilters.tags);
    const filteredThoughts = filterThoughtsBySearchTerm(tagFilteredThoughts, searchTerm);

    // Sort thoughts using the utility function
    const sortedThoughts = sortThoughts(filteredThoughts, sortBy);

    // Handle filter change
    const handleFilterChange = (newFilters) => {
        setActiveFilters(newFilters);
    };

    return (
        <div style={{ width: '100%' }} className="smooth-scroll">
            {/* CSS for responsive layouts only - no animations */}
            <style>
                {`
                    @media (min-width: 768px) {
                        .desktop-layout { display: flex; }
                        .mobile-layout { display: none; }
                    }
                    @media (max-width: 767px) {
                        .desktop-layout { display: none; }
                        .mobile-layout { display: flex; }
                    }
                `}
            </style>
            
            {/* DESKTOP LAYOUT */}
            <div className="desktop-layout" style={{
                flexDirection: 'column',
                width: '100%',
            }}>
                <PageHeader 
                    title={{ text: title || "Library", large: true }}
                    onBack={showBackButton ? onBackClick : null}
                    rightContent={
                        <div style={styles.desktopUtilityBarContainer}>
                            {/* Search bar */}
                            <SearchInput
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search thoughts..."
                                type="desktop"
                            />
                            
                            <ToolsBar
                                onSortClick={() => setShowSortDrawer(true)}
                                onFilterClick={() => setShowFilterDrawer(true)}
                                hasActiveFilters={hasActiveFilters}
                                onAddClick={onAddClick}
                            />
                        </div>
                    }
                />
            </div>
            
            {/* MOBILE LAYOUT */}
            <div className="mobile-layout" style={{
                flexDirection: 'column',
                width: '100%',
            }}>
                {/* Back button row */}
                    {showBackButton && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        marginBottom: '8px',
                    }}>
                        <button onClick={onBackClick} style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                )}
                
                {/* Search and tools row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 16px',
                    marginBottom: '12px',
                }}>
                    {/* Search bar */}
                    <div style={styles.mobileUtilityBarContainer}>
                        <SearchInput
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search thoughts..."
                            type="mobile"
                        />
                    </div>
                    
                    {/* Tools */}
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                        {/* Sort button */}
                        <SortButton
                            onClick={() => setShowSortDrawer(true)}
                        />
                        
                        {/* Filter button */}
                        <button
                            onClick={() => setShowFilterDrawer(true)}
                            aria-label="Filter"
                            style={{
                                background: hasActiveFilters ? `${colors.accent.white}20` : 'none',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                position: 'relative'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke={hasActiveFilters ? 'black' : colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            
                            {/* Indicator dot for active filters */}
                            {hasActiveFilters && (
                                <span style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: colors.accent.white,
                                }}></span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Title and plus button row */}
                <div style={styles.pageHeaderContainer}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '22px',
                        fontWeight: '600',
                        color: colors.textPrimary,
                        lineHeight: '38px',
                    }}>
                        {title || "Library"}
                    </h2>
                    
                    {/* Add button - Only shows if onAddClick prop is provided */}
                    {onAddClick && (
                        <IconButton
                            onClick={onAddClick}
                            icon={<PlusIcon strokeLinecap="butt" strokeLinejoin="miter" />}
                            ariaLabel="Add thought"
                        />
                    )}
                </div>
            </div>

            {/* Horizontal scrollable tag filters */}
            <TagFilters 
                popularTags={popularTags}
                activeFilters={activeFilters.tags}
                toggleTag={toggleTag}
                clearFilters={() => setActiveFilters({ tags: [] })}
            />

            {/* No results state */}
            {(searchTerm || hasActiveFilters) && sortedThoughts.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 0', 
                    color: colors.textSecondary,
                    marginTop: '16px'
                }}>
                    <p>No thoughts match your search or filters.</p>
                </div>
            ) : (
                /* Clean, full-width feed */
                <div className="thoughts-feed" style={{ 
                    display: 'flex', 
                    flexDirection: 'column'
                }} key={activeFilters.tags.join(',') + searchTerm}>
                    <StaggeredAnimation containerClass="thoughts-feed" />
                    {sortedThoughts.map((thought, index) => (
                        <FeedItem 
                            key={thought.id} 
                            thought={thought} 
                            isLast={index === sortedThoughts.length - 1}
                            onDelete={() => onDeleteThought && onDeleteThought(thought.id)}
                        />
                    ))}
                </div>
            )}

            {/* Sort drawer component */}
            <SortDrawer
                isVisible={showSortDrawer}
                onClose={() => setShowSortDrawer(false)}
                onSortChange={handleSortChange}
                currentSort={sortBy}
            />
            
            {/* Filter drawer component */}
            <FilterDrawer
                isVisible={showFilterDrawer}
                onClose={() => setShowFilterDrawer(false)}
                onFilterChange={handleFilterChange}
                currentFilters={activeFilters}
                thoughts={libraryThoughts}
            />
        </div>
    );
};

/**
 * Clean, minimal feed item with swipe functionality
 */
const FeedItem = ({ thought, isLast, onDelete }) => {
    const content = thought.thought || thought.content || '';
    const [swipeState, setSwipeState] = useState({ isOpen: false, direction: null });
    const navigate = useNavigate();
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    
    const handleContextMenu = (e) => {
        // Allow the browser's default context menu to appear
        // Don't call e.preventDefault() or e.stopPropagation() here
        
        // Still set up our custom context menu
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setShowContextMenu(true);
    };
    
    const handleOpenInNewTab = () => {
        const newTabUrl = `${window.location.origin}/thought/${thought.id}`;
        window.open(newTabUrl, '_blank');
        setShowContextMenu(false);
    };
    
    // Close context menu when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = () => {
            setShowContextMenu(false);
        };
        
        if (showContextMenu) {
            document.addEventListener('click', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showContextMenu]);
    
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => setSwipeState({ isOpen: true, direction: 'left' }),
        onSwipedRight: () => setSwipeState({ isOpen: false, direction: 'right' }),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
        delta: 10, // minimum swipe distance
        onSwiping: (eventData) => {
            // If horizontal movement is greater than vertical, prevent default to stop scrolling
            if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY)) {
                eventData.event.preventDefault();
                eventData.event.stopPropagation();
            }
        }
    });
    
    // Add a touch handler to capture touch events at the start
    const handleTouchStart = (e) => {
        // We'll use this as a backup to ensure we can capture and prevent unwanted scrolling
        const initialTouch = e.touches[0];
        const touchStartX = initialTouch.clientX;
        const touchStartY = initialTouch.clientY;
        
        const handleTouchMove = (e) => {
            if (!e.touches.length) return;
            
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            
            // If horizontal movement is greater than vertical, prevent default
            if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2) { // 1.2 multiplier for better detection
                e.preventDefault();
            }
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        
        const cleanup = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', cleanup);
            document.removeEventListener('touchcancel', cleanup);
        };
        
        document.addEventListener('touchend', cleanup, { once: true });
        document.addEventListener('touchcancel', cleanup, { once: true });
    };
    
    // Check if thought has comments
    const hasComments = thought.comments && thought.comments.length > 0;
    
    // Check if we're in a collection path to determine button label
    const location = useLocation();
    const isInCollection = location.pathname.includes('/collection/');
    const isInDefaultCollection = location.pathname.includes('/collection/') && 
                                location.state && location.state.isDefault;
    const buttonLabel = isInCollection && !isInDefaultCollection ? "Remove" : "Delete";
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                overflow: 'hidden',
                willChange: 'transform, opacity', // Optimize for animation performance
            }}
            className="list-item"
        >
            {/* Delete button (revealed when swiped left) */}
            <div 
                onClick={(e) => {
                    e.stopPropagation(); // Prevent bubbling up to the parent click handler
                    onDelete(); // Trigger the delete action
                    setSwipeState({ isOpen: false, direction: null }); // Reset swipe state
                }}
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '80px',
                    backgroundColor: '#FF3B30',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer', // Add cursor pointer to indicate it's clickable
                }}
            >
                {buttonLabel}
            </div>
            
            {/* Main content */}
            <a 
                {...swipeHandlers}
                onTouchStart={handleTouchStart}
                onClick={(e) => {
                    e.preventDefault(); // Prevent default link behavior
                    if (!swipeState.isOpen) {
                        navigate(`/thought/${thought.id}`);
                    }
                }}
                onContextMenu={handleContextMenu}
                href={`/thought/${thought.id}`}
                style={{ 
                    padding: '16px',
                    cursor: 'pointer',
                    width: '100%',
                    backgroundColor: colors.background || 'white',
                    borderBottom: isLast ? 'none' : `1px solid ${colors.divider}`,
                    transform: swipeState.isOpen ? 'translateX(-80px)' : 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)', // Improved transition curve
                    touchAction: 'pan-y', // Allow vertical scrolling by default
                    display: 'flex',
                    alignItems: 'center', // Center align for better text alignment
                    textDecoration: 'none', // Remove underline from link
                    color: 'inherit', // Keep the text color
                }}
            >
                {/* Use Identicon directly with its built-in wrapper */}
                <Identicon seed={thought.id} size={36} />
                
                <div style={{
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    position: 'relative',
                    paddingRight: (thought.importance === undefined || thought.importance === null) ? '16px' : '0',
                    flex: 1,
                }}>
                    {content}
                    {hasComments && (
                        <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '4px',
                            verticalAlign: 'middle' 
                        }}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6h8" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M4 10h10" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M4 14h6" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </span>
                    )}
                    {(thought.importance === undefined || thought.importance === null) && (
                        <span style={{
                            position: 'absolute',
                            right: '0',
                            bottom: '3px',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: colors.accent.white,
                            display: 'inline-block',
                        }}></span>
                    )}
                </div>
                
                {/* Right-aligned star for importance 5 */}
                {thought.importance === 5 && (
                    <svg 
                        style={{ marginLeft: '8px', flexShrink: 0 }}
                        xmlns="http://www.w3.org/2000/svg" 
                        height="14px" 
                        viewBox="0 -960 960 960" 
                        width="14px" 
                        fill="#e3e3e3">
                        <path d="m233-80 65-281L80-550l288-25 112-265 112 265 288 25-218 189 65 281-247-149L233-80Z"/>
                    </svg>
                )}
                
                {/* Sun icon for insights */}
                {thought.insight && (
                    <svg 
                        style={{ marginLeft: '8px', flexShrink: 0 }}
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="12" cy="12" r="4" stroke="#e3e3e3" strokeWidth="2"/>
                        <path d="M12 5V3" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 21V19" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M5 12H3" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M21 12H19" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M7.05 7.05L5.636 5.636" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M18.364 18.364L16.95 16.95" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M7.05 16.95L5.636 18.364" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M18.364 5.636L16.95 7.05" stroke="#e3e3e3" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                )}
            </a>
            
            {/* Context Menu */}
            {showContextMenu && (
                <div
                    style={{
                        position: 'fixed',
                        top: contextMenuPosition.y,
                        left: contextMenuPosition.x,
                        backgroundColor: colors.surfaceElevated,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        padding: '8px 0',
                        zIndex: 1000,
                        minWidth: '160px',
                    }}
                >
                    <div
                        onClick={handleOpenInNewTab}
                        style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: colors.textPrimary,
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.hover}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        Open in new tab
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThoughtsList;